import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { api, type Auswertungsansicht, type Ich, type Kontext } from './api'
import { alsIso, datumLang } from './format'
import { VORGABEN, passendeVorgabe, zeitraumText } from './zeitraum'
import { ThemaSchalter, useThema } from './komponenten/ThemaSchalter'
import { Aenderungsverlauf } from './komponenten/Aenderungsverlauf'
import { Schloss, Zahnrad } from './komponenten/Zeichen'
import { Tipp } from './komponenten/Hinweis'
import { Anmeldung } from './ansichten/Anmeldung'
import { Artikel } from './ansichten/Artikel'
import { Backoffice } from './ansichten/Backoffice'
import { Zusammenstellungen } from './ansichten/Zusammenstellungen'
import { Einrichtung } from './ansichten/Einrichtung'
import { Datenqualitaet } from './ansichten/Datenqualitaet'
import { Standortvergleich } from './ansichten/Standortvergleich'
import { Gaeste } from './ansichten/Gaeste'
import { Gesperrt } from './ansichten/Gesperrt'
import { Pruefmodul } from './ansichten/Pruefmodul'
import { Quoten } from './ansichten/Quoten'
import { Rechnungen } from './ansichten/Rechnungen'
import { Stornos } from './ansichten/Stornos'
import { Tag } from './ansichten/Tag'
import { Verlauf } from './ansichten/Verlauf'
import { Warengruppen } from './ansichten/Warengruppen'
import { Zugaenge } from './ansichten/Zugaenge'
import { Sicherheit } from './ansichten/Sicherheit'
import { Standorte } from './ansichten/Standorte'
import { Import } from './ansichten/Import'
import { Einladung } from './ansichten/Einladung'
import { Warenkorb } from './ansichten/Warenkorb'
import { Auswertungen } from './ansichten/Auswertungen'
import { Stationen } from './ansichten/Stationen'
import { Abbrueche } from './ansichten/Abbrueche'

/* Zwei Werkzeuge, getrennt buchbar, getrennt zu sehen.
 *
 * Oben wird zwischen ihnen gewechselt, links steht das Menü des
 * gewählten. Der Schnitt ist nicht kosmetisch: Er läuft beim
 * Personenbezug. Das Reporting wertet aus, was verkauft wurde; das
 * Audit prüft, wer was getan hat. Deshalb liegen die Stornos in beiden
 * Werkzeugen — im Reporting ohne den Personenteil, und zwar nicht
 * ausgeblendet, sondern nicht abgefragt.
 *
 * Die Verwaltung (Zahnrad) ist ein Zwischenstand. Einrichtung und
 * Datenqualität gehören ins Backoffice, nicht ins Kundenwerkzeug —
 * solange es das nicht gibt, stehen sie hier.
 */

type Werkzeug = 'reporting' | 'audit' | 'verwaltung' | 'backoffice' | 'haus'

type Reiter = 'tag' | 'verlauf' | 'quoten' | 'artikel' | 'stationen' | 'abbrueche' | 'warenkorb'
  | 'zusammen' | 'gaeste' | 'stornos' | 'betriebe'
  | 'rechnungen' | 'pruefung' | 'audit-stornos'
  | 'einrichtung' | 'qualitaet'
  | 'kunden' | 'warengruppen'
  | 'auswertungen'
  | 'import' | 'standorte' | 'zugaenge' | 'sicherheit'

const WERKZEUGE: { wert: 'reporting' | 'audit'; name: string }[] = [
  { wert: 'reporting', name: 'Reporting' },
  { wert: 'audit', name: 'Audit' },
]

const REITER: {
  wert: Reiter; name: string; werkzeug: Werkzeug; nurVerbund?: boolean
}[] = [
  { wert: 'tag',       name: 'Tag',                werkzeug: 'reporting' },
  { wert: 'verlauf',   name: 'Verlauf',            werkzeug: 'reporting' },
  { wert: 'quoten',    name: 'Quoten',             werkzeug: 'reporting' },
  { wert: 'artikel',   name: 'Artikel',            werkzeug: 'reporting' },
  { wert: 'stationen', name: 'Stationen',           werkzeug: 'reporting' },
  { wert: 'abbrueche', name: 'Belegabbrüche',      werkzeug: 'reporting' },
  { wert: 'warenkorb', name: 'Warenkorb',          werkzeug: 'reporting' },
  { wert: 'zusammen',  name: 'Zusammenstellungen', werkzeug: 'reporting' },
  { wert: 'gaeste',    name: 'Gäste & Tische',     werkzeug: 'reporting' },
  { wert: 'stornos',   name: 'Stornos',            werkzeug: 'reporting' },
  { wert: 'betriebe',  name: 'Standortvergleich', werkzeug: 'reporting',
    nurVerbund: true },

  // Die Rechnungen stehen vorn: Sie sind die Grundlage, auf der die
  // Prüfung rechnet. Wer ein Prüfergebnis nicht versteht, sieht hier
  // nach, was an dem Tisch wirklich passiert ist.
  { wert: 'rechnungen',    name: 'Rechnungen',       werkzeug: 'audit' },
  { wert: 'pruefung',      name: 'Rechnungsprüfung', werkzeug: 'audit' },
  { wert: 'audit-stornos', name: 'Stornos',          werkzeug: 'audit' },
  { wert: 'abbrueche',     name: 'Belegabbrüche',  werkzeug: 'audit' },

  { wert: 'einrichtung', name: 'Einrichtung',    werkzeug: 'verwaltung' },
  { wert: 'qualitaet',   name: 'Datenqualität',  werkzeug: 'verwaltung' },

  { wert: 'kunden',      name: 'Kunden',         werkzeug: 'backoffice' },
  { wert: 'warengruppen', name: 'Warengruppen',  werkzeug: 'backoffice' },
  { wert: 'auswertungen', name: 'Auswertungen',   werkzeug: 'backoffice' },

  // Das Haus: was der Kunde selbst macht. Der Import steht vorn — er
  // ist das, wofür jemand hier täglich hereinkommt.
  { wert: 'import',   name: 'Daten einlesen', werkzeug: 'haus' },
  { wert: 'standorte', name: 'Standorte',     werkzeug: 'haus' },
  { wert: 'zugaenge', name: 'Zugänge',        werkzeug: 'haus' },
  { wert: 'sicherheit', name: 'Sicherheit',  werkzeug: 'haus' },
]


function einladungAusDerAdresse(): string | null {
  const m = /^\/einladung\/([A-Za-z0-9_-]{16,})$/.exec(window.location.pathname)
  return m ? m[1] : null
}


export default function App() {
  const [thema, setThema] = useThema()
  const [werkzeug, setWerkzeug] = useState<Werkzeug>('reporting')
  const [reiter, setReiter] = useState<Reiter>('tag')
  const [kontext, setKontext] = useState<Kontext | null>(null)
  const [ansichten, setAnsichten] = useState<Auswertungsansicht[]>([])
  const [fehler, setFehler] = useState<string | null>(null)
  const [betrieb, setStandort] = useState<string>('alle')
  const [datum, setDatum] = useState<string>('')
  /* Der Zeitraum steht als **zwei Daten** da, nicht als Spanne. Vorher
     war er `bis` minus n Tage, und `bis` liess sich nur in der
     Tagesansicht verstellen — den Vormonat hat so niemand gefunden. */
  const [vonWahl, setVonWahl] = useState<string>('')
  const [bisWahl, setBisWahl] = useState<string>('')

  /* Wer fragt — und ob überhaupt jemand.
   *
   * `null` heißt „noch nicht nachgesehen": In diesem Zustand wird weder
   * die Anmeldeseite noch das Dashboard gezeigt. Ohne diese dritte
   * Möglichkeit blitzt die Anmeldeseite bei jedem Laden kurz auf, auch
   * wenn man längst angemeldet ist.
   */
  const [ich, setIch] = useState<Ich | null>(null)
  const [ohneMandant, setOhneMandant] = useState(false)

  const ansichtenLaden = useCallback(() => api.ansichten()
    .then(setAnsichten).catch((e) => setFehler(e.message)), [])

  useEffect(() => {
    api.ich().then(setIch).catch((e) => {
      // 404 heißt hier etwas Bestimmtes: Die laufende API kennt
      // `/api/ich` nicht, ist also älter als diese Oberfläche. Das
      // passiert nach einem Dateiabgleich, bei dem der Server weiterlief.
      setFehler(e?.status === 404
        ? 'Die laufende Auswertung ist älter als diese Oberfläche — sie '
          + 'kennt die Anmeldung noch nicht. Das Fenster mit dem Server '
          + 'schließen (Strg+C) und START.bat erneut starten.'
        : e.message)
    })
  }, [])

  /* Den Kontext holen — beim Anmelden und nach jedem Import.
   *
   * Nach dem Import ist das nicht kosmetisch: Vorher war der Zeitraum
   * leer, also stand überall „Es liegen noch keine Geschäftstage vor",
   * und das blieb auch stehen, nachdem die Daten drin waren. Wer das
   * sieht, lädt die Dateien ein zweites Mal hoch. Der zweite Aufruf
   * springt deshalb auch auf den neuesten Tag: Wer gerade eingelesen
   * hat, will den sehen und nicht den, der vorher eingestellt war.
   */
  const kontextLaden = useCallback(() => {
    if (!ich?.angemeldet) return
    setFehler(null)
    Promise.all([api.kontext(), api.ansichten()])
      .then(([k, a]) => {
        setKontext(k)
        setAnsichten(a)
        setOhneMandant(false)
        if (k.zeitraum.bis) {
          setDatum(k.zeitraum.bis)
          /* Vorgabe ist der **Monat des neuesten Geschäftstags**, nicht
             der laufende Kalendermonat: Wer alte Daten einliest, bekäme
             sonst eine leere Ansicht und suchte den Fehler im Import. */
          setVonWahl(k.zeitraum.bis.slice(0, 7) + '-01')
          setBisWahl(k.zeitraum.bis)
        }
        // Wer nur das Audit gebucht hat, soll nicht im gesperrten
        // Reporting landen.
        if (!k.mandant.module_dashboard && k.mandant.module_audit) {
          setWerkzeug('audit'); setReiter('pruefung')
        }
      })
      .catch((e) => {
        // 409 heißt: angemeldet, aber kein Kunde ausgewählt. Das ist
        // beim Verwalter der Normalfall nach dem Anmelden und kein
        // Fehler — er landet im Backoffice und wählt einen.
        if (e?.status === 409) {
          setOhneMandant(true)
          setAnsichten([])
          setWerkzeug('backoffice'); setReiter('kunden')
        } else setFehler(e.message)
      })
  }, [ich])

  useEffect(kontextLaden, [kontextLaden])

  useLayoutEffect(() => {
    if (!ansichten.length) return
    const werkzeugName = werkzeug.toUpperCase()
    const aktuell = ansichten.find((a) => a.schluessel === reiter
      && a.werkzeuge.includes(werkzeugName as 'REPORTING' | 'AUDIT'))
    if (!aktuell || aktuell.aktiv) return
    const erste = REITER.find((r) => {
      if (r.werkzeug !== werkzeug) return false
      const a = ansichten.find((x) => x.schluessel === r.wert
        && x.werkzeuge.includes(werkzeugName as 'REPORTING' | 'AUDIT'))
      return !a || a.aktiv
    })
    if (erste) setReiter(erste.wert)
  }, [ansichten, reiter, werkzeug])

  const abmelden = async () => {
    await api.abmelden().catch(() => undefined)
    setIch({ angemeldet: false })
    setKontext(null)
    setAnsichten([])
  }

  const mandantWaehlen = async (id: string) => {
    await api.verwaltung.waehlen(id)
    // Neu laden statt den Zustand nachzuziehen: Mit dem Mandanten
    // wechselt alles — Standorte, Zeitraum, Buchungen.
    setKontext(null)
    setAnsichten([])
    setIch({ ...(ich as Ich), mandant: id })
    // Und hinüber zur Auswertung. „Auswählen" heißt: diesen Kunden
    // ansehen — im Backoffice stehen zu bleiben sähe aus, als wäre
    // nichts passiert.
    setWerkzeug('reporting'); setReiter('tag')
  }

  const betriebe = useMemo(
    () => (betrieb === 'alle' ? [] : [betrieb]), [betrieb])

  /* Der Fehler steht **vor** der Ladeanzeige.
   *
   * Stand er dahinter, blieb die Seite bei „Wird geladen …" stehen,
   * sobald `/api/ich` scheiterte: `ich` bleibt dann null, und die
   * Ladeanzeige kam nie bis zur Fehlermeldung. Gemeldet als „Tut sich
   * nichts" — und zu sehen war auch nichts.
   */
  /* Der Einladungslink kommt **vor** der Anmeldung.
   *
   * Wer ihn öffnet, hat noch kein Passwort — ihn auf die Anmeldeseite
   * zu schicken wäre die eine Seite, auf der er garantiert nicht
   * weiterkommt. Ein Pfad statt einer Fragezeichen-Angabe, damit das
   * Merkmal nicht in Verläufen und Protokollen landet, wo eine Anfrage
   * mit Parametern gern steht.
   */
  const einladung = einladungAusDerAdresse()
  if (einladung) {
    return <Einladung merkmal={einladung} fertig={(i) => {
      window.history.replaceState(null, '', '/')
      setIch(i)
    }} />
  }

  if (fehler) {
    return (
      <div className="huelle" style={{ paddingTop: 60 }}>
        <div className="fehler">
          <b>Keine Verbindung zur Auswertung.</b>
          <div style={{ marginTop: 6 }}>{fehler}</div>
        </div>
      </div>
    )
  }

  if (ich === null) {
    return <div className="laedt" style={{ paddingTop: 80 }}>Wird geladen …</div>
  }
  if (!ich.angemeldet) return <Anmeldung fertig={setIch} />
  if (!kontext && !ohneMandant) {
    return <div className="laedt" style={{ paddingTop: 80 }}>Wird geladen …</div>
  }

  const leer = !kontext?.zeitraum.bis
  const bis = datum || kontext?.zeitraum.bis || ''
  const von = vonWahl || bis
  const bisZeitraum = bisWahl || bis
  const heute = alsIso(new Date())
  const gedrueckt = passendeVorgabe(von, bisZeitraum, heute)

  /* Ein Zeitraum wird nie verdreht: Wer den Beginn hinter das Ende
     schiebt, zieht das Ende mit. Ein Fehlerzustand mehr waere ein
     Zustand mehr, den jemand wieder verlassen muss. */
  const vonSetzen = (v: string) => {
    setVonWahl(v)
    if (v && bisZeitraum && v > bisZeitraum) setBisWahl(v)
  }
  const bisSetzen = (b: string) => {
    setBisWahl(b)
    if (b && von && b < von) setVonWahl(b)
  }
  const vorgabeWaehlen = (schluessel: string) => {
    const v = VORGABEN.find((x) => x.schluessel === schluessel)
    if (!v) return
    const z = v.rechnen(heute)
    setVonWahl(z.von); setBisWahl(z.bis)
  }
  const istDemo = kontext?.mandant?.code === 'demo'
                  || !!kontext?.mandant?.name?.startsWith('DEMO')
  const betriebszahl = kontext?.betriebe.length ?? 0
  const istVerwalter = ich.rolle === 'ADMIN'

  /* Gebucht oder nicht — und die dritte Möglichkeit.
   *
   * Ohne gewählten Kunden ist die Frage nicht zu beantworten. Ein
   * Schloss wäre dort eine Aussage, die niemand geprüft hat („nicht
   * gebucht"), obwohl nur noch niemand gewählt wurde. Deshalb erst gar
   * kein Schloss, solange kein Mandant geladen ist; wer trotzdem
   * klickt, landet auf dem Hinweis, einen Kunden zu wählen.
   */
  const gebucht = (w: Werkzeug) =>
    !kontext ? true
      : w === 'reporting' ? !!kontext.mandant?.module_dashboard
        : w === 'audit' ? !!kontext.mandant?.module_audit
          : true

  const ansichtNachSchluessel = new Map(ansichten.map((a) => [a.schluessel, a]))
  const menueFuer = (w: Werkzeug) => REITER
    .filter((r) => r.werkzeug === w)
    .filter((r) => !r.nurVerbund || betriebszahl > 1)
    .map((r, index) => {
      const a = ansichtNachSchluessel.get(
        r.wert as Auswertungsansicht['schluessel'])
      const passt = a?.werkzeuge.includes(w.toUpperCase() as 'REPORTING' | 'AUDIT')
      const katalogansicht = passt ? a : undefined
      return {
        ...r, name: katalogansicht?.name ?? r.name,
        beschreibung: katalogansicht?.beschreibung,
        aktiv: katalogansicht?.aktiv ?? true,
        kataloggefuehrt: !!katalogansicht,
        index,
      }
    })
    .sort((a, b) => Number(b.aktiv) - Number(a.aktiv) || a.index - b.index)

  const menue = menueFuer(werkzeug)
  const aktiveMenuepunkte = menue.filter((r) => r.aktiv)

  const wechsle = (w: Werkzeug) => {
    setWerkzeug(w)
    const erste = menueFuer(w).find((r) => r.aktiv)
    if (erste) setReiter(erste.wert)
  }

  // Die Einrichtung steht vor den Daten: Sie soll gerade dann zu sehen
  // sein, wenn noch kein Geschäftstag vorliegt.
  // Zwei Ansichten fragen einen **Tag**, nicht einen Zeitraum: die
  // Tagesansicht und die Rechnungen. Beide bekommen deshalb das
  // Datumsfeld statt der Zeitraumwahl.
  const einzeltag = reiter === 'tag' || reiter === 'rechnungen'
  const ohneZeitraum = reiter === 'einrichtung'
  const offen = gebucht(werkzeug)

  return (
    <>
      <header className="kopf">
        <div className="kopf-innen">
          <div className="marke">
            <b>Gastro Audit</b>
            <span>{kontext ? kontext.mandant.name : 'Verwaltung'}</span>
          </div>

          <nav className="werkzeuge" aria-label="Werkzeug">
            {WERKZEUGE.map((w) => (
              <Tipp key={w.wert} text={gebucht(w.wert) ? w.name
                : `${w.name} ist für diesen Mandanten nicht gebucht`}>
                <button aria-current={werkzeug === w.wert ? 'page' : undefined}
                        className={gebucht(w.wert) ? undefined : 'gesperrt'}
                        onClick={() => wechsle(w.wert)}>
                  {w.name}
                  {!gebucht(w.wert) && <Schloss />}
                </button>
              </Tipp>
            ))}
          </nav>

          <span className="schieber" />

          <Tipp text="Kassendaten einlesen, Zugänge verwalten">
            <button className={`backoffice-knopf${werkzeug === 'haus' ? ' an' : ''}`}
                    aria-current={werkzeug === 'haus' ? 'page' : undefined}
                    onClick={() => wechsle('haus')}>Mein Haus</button>
          </Tipp>

          {istVerwalter && (
            <Tipp text="Kunden verwalten, Stammdaten einspielen">
              <button className={`backoffice-knopf${werkzeug === 'backoffice' ? ' an' : ''}`}
                      aria-current={werkzeug === 'backoffice' ? 'page' : undefined}
                      onClick={() => wechsle('backoffice')}>Backoffice</button>
            </Tipp>
          )}

          <Tipp text="Verwaltung">
            <button className={`zahnrad${werkzeug === 'verwaltung' ? ' an' : ''}`}
                    aria-label="Verwaltung"
                    aria-current={werkzeug === 'verwaltung' ? 'page' : undefined}
                    onClick={() => wechsle('verwaltung')}><Zahnrad /></button>
          </Tipp>
          <ThemaSchalter thema={thema} setThema={setThema} />
          <Tipp text={`Angemeldet als ${ich.email}`}>
            <button className="abmelden"
                    onClick={() => void abmelden()}>Abmelden</button>
          </Tipp>
        </div>
      </header>

      <main className="huelle">
        {istDemo && (
          <div className="demo-band">
            <b>Demodaten.</b> Erzeugte Zahlen aus der Form eines echten
            Geschäftstags — kein echter Standort, keine echten Umsätze. Sie sind dafür da,
            das Verhalten bei zwölf Standorten prüfbar zu machen.
          </div>
        )}

        {werkzeug === 'haus' ? (
          <div className="raster">
            <nav className="seitenmenue" aria-label="Bereich">
              {menue.map((r) => (
                <button key={r.wert}
                        aria-current={reiter === r.wert ? 'page' : undefined}
                        onClick={() => setReiter(r.wert)}>{r.name}</button>
              ))}
              <p className="menue-fuss">
                Stammdaten — Artikelliste und Warengruppen — pflegt der
                Betreiber. Hier stehen die täglichen Dateien und die
                Zugänge.
              </p>
              <Aenderungsverlauf />
            </nav>
            <div className="inhalt">
              {/* Sicherheit hängt am eigenen Konto und nicht am
                  Mandanten — der Betreiber muss seinen zweiten Faktor
                  auch dann verwalten können, wenn er gerade keinen
                  Kunden gewählt hat. */}
              {reiter === 'sicherheit' ? <Sicherheit />
                : ohneMandant ? (
                <div className="karte"><div className="leer">
                  Erst einen Kunden wählen.
                </div></div>
              ) : reiter === 'zugaenge' ? <Zugaenge />
                : reiter === 'standorte' ? <Standorte />
                  : <Import nachImport={kontextLaden} />}
            </div>
          </div>
        ) : werkzeug === 'backoffice' ? (
          <div className="raster">
            <nav className="seitenmenue" aria-label="Bereich">
              {menue.map((r) => (
                <button key={r.wert}
                        aria-current={reiter === r.wert ? 'page' : undefined}
                        onClick={() => setReiter(r.wert)}>{r.name}</button>
              ))}
              <p className="menue-fuss">
                Die Warengruppen gehören zum gewählten Kunden — erst
                wählen, dann pflegen.
              </p>
              <Aenderungsverlauf />
            </nav>
            <div className="inhalt">
              {reiter === 'auswertungen' && !ohneMandant ? (
                <Auswertungen nachAnsichtAenderung={() => void ansichtenLaden()} />
              )
                : reiter === 'auswertungen' ? (
                  <div className="karte"><div className="leer">
                    Erst einen Kunden wählen — die Kacheln gelten je Mandant.
                  </div></div>
                ) : reiter === 'warengruppen' && !ohneMandant ? <Warengruppen />
                : reiter === 'warengruppen' ? (
                  <div className="karte"><div className="leer">
                    Erst einen Kunden wählen — Warengruppen gehören zu einem
                    Mandanten.
                  </div></div>
                ) : (
                  <Backoffice gewaehlterMandant={ich.mandant ?? null}
                              waehlen={(id) => void mandantWaehlen(id)} />
                )}
            </div>
          </div>
        ) : ohneMandant ? (
          <div className="karte"><div className="leer">
            Es ist kein Kunde ausgewählt. Im <b>Backoffice</b> einen
            wählen — danach zeigen alle Auswertungen dessen Zahlen.
          </div></div>
        ) : !offen ? (
          <Gesperrt werkzeug={werkzeug as 'reporting' | 'audit'} />
        ) : (
          <div className="raster">
            <nav className="seitenmenue" aria-label="Bereich">
              {menue.map((r) => r.aktiv ? (
                <button key={r.wert}
                        aria-current={reiter === r.wert ? 'page' : undefined}
                        onClick={() => setReiter(r.wert)}>{r.name}</button>
              ) : (
                <Tipp key={`${r.wert}-${r.werkzeug}`} fokus text={
                  `${r.beschreibung} ${istVerwalter
                    ? 'Einschalten: Backoffice → Auswertungen.'
                    : 'Ihre Betreuung kann diese Ansicht freischalten.'}`
                }>
                  <button className="ansicht-aus" aria-disabled="true"
                          onClick={() => undefined}>{r.name}</button>
                </Tipp>
              ))}
              {werkzeug === 'verwaltung' && (
                <p className="menue-fuss">
                  Beides gehört ins Backoffice und steht hier, solange es
                  das nicht gibt.
                </p>
              )}
              <Aenderungsverlauf />
            </nav>

            <div className="inhalt">
              {!aktiveMenuepunkte.length ? (
                <div className="karte"><div className="leer">
                  <b>Für dieses Werkzeug sind keine Auswertungen eingeschaltet.</b>
                  {istVerwalter ? (
                    <div style={{ marginTop: 10 }}>
                      <button className="verweis" onClick={() => {
                        setWerkzeug('backoffice'); setReiter('auswertungen')
                      }}>Backoffice → Auswertungen</button>
                    </div>
                  ) : (
                    <div style={{ marginTop: 6 }}>
                      Ihre Betreuung kann die Auswertungen freischalten.
                    </div>
                  )}
                </div></div>
              ) : leer && !ohneZeitraum ? (
                <div className="karte"><div className="leer">
                  Es liegen noch keine Geschäftstage vor. Sobald ein Kassenarchiv
                  eingelesen ist, erscheinen hier die Zahlen.
                </div></div>
              ) : (
                <>
                  {!ohneZeitraum && (
                    <div className="leiste">
                      {betriebszahl > 1 ? (
                        <select value={betrieb}
                                onChange={(e) => setStandort(e.target.value)}
                                aria-label="Standort">
                          <option value="alle">
                            Alle {betriebszahl} Standorte
                          </option>
                          {(kontext?.betriebe ?? []).map((b) => (
                            <option key={b.id} value={b.id}>{b.name}</option>
                          ))}
                        </select>
                      ) : betriebszahl === 1 ? (
                        /* Ein Haus mit einem Standort hat nichts zu
                           wählen — aber wissen will man trotzdem,
                           welcher es ist. Stand hier nichts, sah es
                           aus, als säße man noch „in der Zentrale"
                           und käme nicht in den Standort hinein. */
                        <span className="betriebsname">
                          {kontext?.betriebe[0]?.name}
                        </span>
                      ) : null}

                      {einzeltag ? (
                        <>
                          <input type="date" value={bis}
                                 min={kontext?.zeitraum.von ?? undefined}
                                 max={kontext?.zeitraum.bis ?? undefined}
                                 onChange={(e) => setDatum(e.target.value)}
                                 aria-label="Geschäftstag" />
                          <span className="schieber" />
                          <span className="dezent" style={{ fontSize: 12 }}>
                            {datumLang(bis)}
                          </span>
                        </>
                      ) : (
                        <div className="zeitraumwahl">
                          <div className="zeitraumfelder">
                            <label>
                              von
                              <input type="date" value={von}
                                     onChange={(e) => vonSetzen(e.target.value)}
                                     aria-label="Zeitraum von" />
                            </label>
                            <label>
                              bis
                              <input type="date" value={bisZeitraum}
                                     onChange={(e) => bisSetzen(e.target.value)}
                                     aria-label="Zeitraum bis" />
                            </label>
                          </div>
                          {/* Die Knöpfe setzen die beiden Felder, sie sind
                              kein zweiter Weg daneben: Man sieht immer,
                              welcher Zeitraum gerade gilt. */}
                          <div className="knopfleiste" role="group"
                               aria-label="Zeitraum wählen">
                            {VORGABEN.map((v) => (
                              <button key={v.schluessel}
                                      aria-pressed={gedrueckt === v.schluessel}
                                      onClick={() => vorgabeWaehlen(v.schluessel)}>
                                {v.name}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {!einzeltag && !ohneZeitraum && (
                    <p className="zeitraumtext">
                      {zeitraumText(von, bisZeitraum)}
                      {kontext?.zeitraum.bis && von > kontext.zeitraum.bis && (
                        <span className="warn">
                          {' '}— dafür liegen keine Geschäftstage vor
                        </span>
                      )}
                    </p>
                  )}

                  {reiter === 'tag' && <Tag datum={bis} betriebe={betriebe} />}
                  {reiter === 'verlauf' && <Verlauf von={von} bis={bisZeitraum} betriebe={betriebe} />}
                  {reiter === 'quoten' && <Quoten von={von} bis={bisZeitraum} betriebe={betriebe} />}
                  {reiter === 'artikel' && <Artikel von={von} bis={bisZeitraum} betriebe={betriebe} />}
                  {reiter === 'stationen' && <Stationen von={von} bis={bisZeitraum} betriebe={betriebe} />}
                  {reiter === 'abbrueche' && (
                    <Abbrueche von={von} bis={bisZeitraum} betriebe={betriebe}
                               audit={werkzeug === 'audit'} />
                  )}
                  {reiter === 'warenkorb' && <Warenkorb von={von} bis={bisZeitraum} betriebe={betriebe} />}
                  {reiter === 'zusammen' && <Zusammenstellungen von={von} bis={bisZeitraum} betriebe={betriebe} />}
                  {reiter === 'gaeste' && <Gaeste von={von} bis={bisZeitraum} betriebe={betriebe} />}
                  {reiter === 'stornos' && (
                    <Stornos von={von} bis={bisZeitraum} betriebe={betriebe} modul="reporting" />
                  )}
                  {reiter === 'betriebe' && <Standortvergleich von={von} bis={bisZeitraum} />}

                  {reiter === 'rechnungen' && <Rechnungen datum={bis} betriebe={betriebe} />}
                  {reiter === 'pruefung' && <Pruefmodul von={von} bis={bisZeitraum} betriebe={betriebe} />}
                  {reiter === 'audit-stornos' && (
                    <Stornos von={von} bis={bisZeitraum} betriebe={betriebe} modul="audit" />
                  )}

                  {reiter === 'qualitaet' && <Datenqualitaet von={von} bis={bisZeitraum} betriebe={betriebe} />}
                </>
              )}

              {ohneZeitraum && <Einrichtung />}
            </div>
          </div>
        )}
      </main>
    </>
  )
}
